import React, { useState, useEffect } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import { Info, WorkflowIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { WorkflowService, Workflow } from "../../services/WorkflowService";
import { WorkflowActionConfig } from "../../types/AgentAction";

interface AgentActionWorkflowEditProps {
  config: WorkflowActionConfig;
  onChange: (config: WorkflowActionConfig) => void;
  actionName?: string;
  description?: string;
  onActionNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  showSaveActions?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isValid?: boolean;
}

const AgentActionWorkflowEdit: React.FC<AgentActionWorkflowEditProps> = ({
  config,
  onChange,
  actionName,
  description,
  onActionNameChange,
  onDescriptionChange,
}) => {
  const { apiKey } = usePermissions();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowRefOverride, setWorkflowRefOverride] = useState(
    config.workflow_ref || ""
  );
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>("");

  useEffect(() => {
    setWorkflowRefOverride(config.workflow_ref || config.workflow_ref_name || "");
  }, [config.workflow_ref, config.workflow_ref_name]);

  useEffect(() => {
    setWorkflowsLoading(true);
    WorkflowService.getWorkflows(setWorkflowsLoading, apiKey).subscribe(
      (list: Workflow[]) => {
        const arr = Array.isArray(list) ? list : [];
        setWorkflows(arr);
        if (config.workflow_ref || config.workflow_ref_name) {
          const ref = (config.workflow_ref || config.workflow_ref_name || "").replace(/^workflow:/, "");
          const match = arr.find(
            (w) =>
              w.workflow_ref_name === ref ||
              (w.workflow_ref_name && config.workflow_ref?.endsWith(w.workflow_ref_name)) ||
              (w.workflow_name && config.workflow_ref?.includes(w.workflow_name))
          );
          if (match) setSelectedWorkflowId(String(match.id));
        }
        setWorkflowsLoading(false);
      },
      () => {
        setWorkflows([]);
        setWorkflowsLoading(false);
      }
    );
  }, [apiKey]);

  const updateConfig = (updates: Partial<WorkflowActionConfig>) => {
    onChange({ ...config, ...updates });
  };

  const handleWorkflowSelect = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    if (!workflowId) {
      updateConfig({ workflow_ref: workflowRefOverride || undefined, workflow_ref_name: undefined });
      return;
    }
    const w = workflows.find((x) => String(x.id) === workflowId);
    if (w) {
      const ref = w.workflow_ref_name ? `workflow:${w.workflow_ref_name}` : undefined;
      updateConfig({
        workflow_ref: ref || `workflow:${w.workflow_name}`,
        workflow_ref_name: w.workflow_ref_name,
      });
      setWorkflowRefOverride(w.workflow_ref_name || w.workflow_name || "");
    }
  };

  const handleRefOverrideChange = (value: string) => {
    setWorkflowRefOverride(value);
    const ref = value.trim() ? (value.startsWith("workflow:") ? value : `workflow:${value}`) : undefined;
    updateConfig({
      workflow_ref: ref,
      workflow_ref_name: value.trim() || undefined,
    });
    if (value.trim()) setSelectedWorkflowId("");
  };

  return (
    <div className="space-y-6">
      {onActionNameChange && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-gc-med text-foreground">
              Action Name
            </Label>
            <Input
              value={actionName || ""}
              onChange={(e) => onActionNameChange(e.target.value)}
              placeholder="e.g., Run HS Classification"
            />
          </div>
          {onDescriptionChange && (
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">
                Description
              </Label>
              <Textarea
                value={description || ""}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Describe what this workflow does and when to use it"
                className="min-h-[80px]"
              />
            </div>
          )}
        </div>
      )}

      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center gap-2">
          <WorkflowIcon className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-gc-med text-foreground">
            Workflow Configuration
          </h3>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-gc-med text-foreground">
              Workflow <span className="text-red-500">*</span>
            </Label>
            <Tooltip content="Select a Maitai Workflow to run when this action is invoked. The workflow receives the action parameters as its input.">
              <Info className="w-4 h-4 text-muted-foreground" />
            </Tooltip>
          </div>
          {workflowsLoading ? (
            <div className="text-sm text-muted-foreground">Loading workflows...</div>
          ) : workflows.length > 0 ? (
            <Select
              value={selectedWorkflowId || (config.workflow_ref ? "custom" : "")}
              onValueChange={(v) => {
                if (v === "custom") {
                  setSelectedWorkflowId("");
                  return;
                }
                handleWorkflowSelect(v);
              }}>
              <SelectTrigger>
                <SelectValue placeholder="Select a workflow" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">
                  <span className="text-muted-foreground">
                    Enter ref manually (below)
                  </span>
                </SelectItem>
                {workflows.map((w) => (
                  <SelectItem key={w.id} value={String(w.id)}>
                    <div className="flex flex-col">
                      <span className="font-gc-med">{w.workflow_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {w.workflow_ref_name || `ID ${w.id}`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Input
            value={workflowRefOverride}
            onChange={(e) => handleRefOverrideChange(e.target.value)}
            placeholder="e.g., my-workflow or workflow:my-workflow"
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Workflow ref is passed to the workflow service as the model (e.g. workflow:your-ref).
            If you selected a workflow above, this field is set automatically.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">
            The agent will pass action parameters as the workflow input. You can define
            parameters in the Usage Guidance section (When to use / References) or in
            the action&apos;s meta; the orchestrator will collect values and send them
            as the workflow <code className="text-foreground">input</code>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentActionWorkflowEdit;
